import TestIdContext from './TestIdContext.enum';
import { toCamelCaseForCy, toSnakeCaseForTestId } from '../../utils/formatString';

type AttributeValue = string | number | boolean;
export type TestingAttributes = Record<string, AttributeValue>;

const toKebabCase = (str: string): string => str.replace(/_/g, '-');

export interface IGeneratedTestIds {
  autocomplete: string;
  option: string;
  input: string;
  inputField: string;
  chipSelected: string;
  chipUnselected: string;
}

export const generateTestIds = (context: TestIdContext): IGeneratedTestIds => ({
  autocomplete: `${context}_search_autocomplete`,
  option: `${context}_search_option`,
  input: `${context}_search_input`,
  inputField: `${context}_search_input_field`,
  chipSelected: `selected_${context}_chip`,
  chipUnselected: `${context}_chip`,
});

export const getCyAttributes = (
  context: TestIdContext,
  data: { id: string; name: string; selected: boolean },
): Record<string, string | boolean> => {
  const kebabContext = toKebabCase(context);

  return {
    [`data-cy-${kebabContext}-id`]: toCamelCaseForCy(data.id),
    [`data-cy-${kebabContext}-name`]: toCamelCaseForCy(data.name),
    [`data-cy-${kebabContext}-selected`]: toCamelCaseForCy(data.selected.toString()),
  };
};

export const testId = (context: TestIdContext, element: string, variant?: string): string => {
  const parts = [context, element];

  if (variant) {
    parts.push(variant);
  }

  return toSnakeCaseForTestId(parts.join('_'));
};

/**
 * Genera data-testid y opcionalmente data-cy-* attributes.
 * @example
 * {...getTestAttrs(TestIdContext.SKILLS_KANBAN, 'card', { skillId: '123', name: 'React' })}
 * // => { 'data-testid': 'skills_card', 'data-cy-skills-kanban-skillid': '123', ... }
 */
export const getTestAttrs = <T extends Record<string, unknown>>(
  context: TestIdContext,
  element: string,
  data?: T,
  variant?: string,
): TestingAttributes => {
  const attrs: TestingAttributes = {
    'data-testid': testId(context, element, variant),
  };

  if (data) {
    const kebabContext = toKebabCase(context);

    Object.entries(data).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        const attrName = `data-cy-${kebabContext}-${key.toLowerCase()}`;

        if (typeof value === 'object') {
          attrs[attrName] = JSON.stringify(value);
        } else if (typeof value === 'string') {
          attrs[attrName] = toCamelCaseForCy(value);
        } else {
          attrs[attrName] = String(value);
        }
      }
    });
  }

  return attrs;
};
